export enum PicklistSelectionDecisionRule {
  // Both transcripts independently resolve to the same picklist option index.
  Consensus = 'consensus',
  // Exactly one transcript resolves to an option; the other resolves to no match.
  ExactOverNone = 'exact_over_none',
  // Exactly one transcript resolves via fuzzy matching; guarded by confidence/margin thresholds.
  GuardedFuzzy = 'guarded_fuzzy',
  // Both transcripts resolve, but to different option indices.
  Conflict = 'conflict',
  // Neither transcript resolves to a usable option index.
  InsufficientConfidence = 'insufficient_confidence',
}

export enum PicklistSelectionMatchType {
  None = 'none',
  Exact = 'exact',
  Fuzzy = 'fuzzy',
}

export enum PicklistSelectionCommandMode {
  Numeric = 'numeric',
  NameWithTrigger = 'name_with_trigger',
  NameTriggerless = 'name_triggerless',
}

export type GetPicklistOptionSelectionMatchOptions = {
  picklistId?: string | null;
  allowTriggerlessOptionNameMatch?: boolean;
};

export type PicklistSelectionMatchCandidate = {
  preMatchText: string;
  pickIndex: number;
  postMatchText: string;
};

export type PicklistOptionSelectionFuzzyMatch = Readonly<{
  preMatchText: string;
  pickIndex: number;
  postMatchText: string;
  confidence: number;
  margin: number;
}>;

export type GetPicklistOptionSelectionFuzzyMatch = (
  text: string,
  options?: GetPicklistOptionSelectionMatchOptions
) => Nullable<PicklistOptionSelectionFuzzyMatch>;

export type PicklistSelectionMatchCandidates = {
  stableSelectionMatch: PicklistSelectionMatchCandidate | null;
  advisorSelectionMatch: PicklistSelectionMatchCandidate | null;
  stableMatchType: PicklistSelectionMatchType;
  advisorMatchType: PicklistSelectionMatchType;
  stablePickIndex: number | null;
  advisorPickIndex: number | null;
  stableConfidence: number;
  advisorConfidence: number;
  stableMargin: number;
  advisorMargin: number;
};

// Exact matches should behave as deterministic selections, so we keep this near 1.0.
// This also gives room to later carry non-perfect exact-confidence signals from upstream models.
export const EXACT_CONFIDENCE_THRESHOLD = 0.95;
// Fuzzy acceptance is intentionally stricter than "best effort": 0.72 balances recovery for common
// ASR miss-hears without over-triggering on unrelated dictation.
export const FUZZY_CONFIDENCE_THRESHOLD = 0.72;
// Margin prevents ambiguous fuzzy winners. 0.08 requires a clear lead over the next-best option.
export const FUZZY_MARGIN_THRESHOLD = 0.08;
